import type { ReactNode } from "react";
import { Globe, Link2 } from "lucide-react";
import type { EntitySocialLinksFields } from "@/lib/entitySocialLinks";
import { normalizeExternalHref } from "@/lib/entitySocialLinks";

function IconFacebook({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

function IconInstagram({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
      />
    </svg>
  );
}

function IconYoutube({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
      />
    </svg>
  );
}

type Props = {
  links: Partial<EntitySocialLinksFields> | null | undefined;
  className?: string;
};

const linkBtnClass =
  "inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-gray/80 bg-white text-brand-blue shadow-sm transition hover:border-brand-maroon hover:text-brand-maroon focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-maroon";

/**
 * Renders outbound icon links for entity social fields (branch / batch / association pages).
 */
export function EntitySocialLinkIcons({ links, className }: Props) {
  if (!links) return null;

  const entries: { href: string; label: string; node: ReactNode }[] = [];

  const push = (raw: string | null | undefined, label: string, node: React.ReactNode) => {
    const href = normalizeExternalHref(raw);
    if (href) entries.push({ href, label, node });
  };

  push(links.social_facebook_url, "Facebook", <IconFacebook className="h-5 w-5" />);
  push(links.social_instagram_url, "Instagram", <IconInstagram className="h-5 w-5" />);
  push(links.social_youtube_url, "YouTube", <IconYoutube className="h-5 w-5" />);
  push(links.social_website_url, "Website", <Globe className="h-5 w-5" strokeWidth={2} />);
  push(links.social_other_url, "Link", <Link2 className="h-5 w-5" strokeWidth={2} />);

  if (entries.length === 0) return null;

  return (
    <ul className={`flex flex-wrap gap-2 ${className ?? ""}`} aria-label="Social and web links">
      {entries.map(({ href, label, node }) => (
        <li key={label}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkBtnClass}
            aria-label={label}
            title={label}
          >
            {node}
          </a>
        </li>
      ))}
    </ul>
  );
}
